/* eslint-disable react/jsx-key */
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, EyeInvisibleOutlined, EyeOutlined, FileOutlined, LinkOutlined, MoreOutlined, ReloadOutlined, StarOutlined, StopOutlined, VerticalAlignTopOutlined } from '@ant-design/icons';
import { Popconfirm } from 'antd';
import { Dropdown } from 'antd';
import { Tag } from 'antd';
import React, { useMemo } from 'react';


// Every distinct hue antd ships as a Tag preset. `pink` and `grey` are aliases of
// `magenta`/`default`, so they are left out.
const PRESET_COLORS = [ 'magenta', 'red', 'volcano', 'orange', 'gold', 'yellow', 'lime', 'green', 'cyan', 'blue', 'geekblue', 'purple' ];

// antd has no more presets to give, so these fill the widest gaps between the preset
// hues (green->cyan, cyan->blue, geekblue->purple, purple->magenta, magenta->red).
// Each palette was generated with antd's own algorithm and uses the same level-1 /
// level-3 / level-6 shades a preset does, so they render at the same weight instead
// of the solid fill a raw hex passed to `color` would produce.
const CUSTOM_COLORS = {
    jade: { background: '#e8ffec', borderColor: '#89e89f', color: '#16c350' },
    sky: { background: '#e6fcff', borderColor: '#91e9ff', color: '#15a3e1' },
    indigo: { background: '#f4f0ff', borderColor: '#c1b0ff', color: '#432fde' },
    orchid: { background: '#fff0fd', borderColor: '#ffb0fc', color: '#d22fde' },
    rose: { background: '#fff0f2', borderColor: '#ffa6b8', color: '#f02964' },
};

const colors = [ ...PRESET_COLORS, ...Object.keys( CUSTOM_COLORS ) ];
export function getHashedColor( value ) {
    const str = JSON.stringify( value ) ?? '';
    let hash = 0;
    for ( let i = 0; i < str.length; i++ ) {
        hash = ( hash * 31 + str.charCodeAt( i ) ) | 0;
    }
    return colors[ Math.abs( hash ) % colors.length ];
}


export const ColoredTag = ( { value, children, icon, style, ...props } ) => {

    const color = useMemo( () => getHashedColor( value ), [ value ] );
    const custom = CUSTOM_COLORS[ color ];

    return <Tag { ...props } color={ custom ? undefined : color } style={ custom ? { ...custom, ...style } : style } icon={ icon }>{ children ?? value }</Tag>;
};

// rest props (and ref) are passed through so a wrapping Tooltip/Popover can attach its handlers
const PublishTag = ( { isPublished, mini = false, ...props } ) => {

    return isPublished ?
        <Tag { ...props } color='green' icon={ <EyeOutlined /> }>{ mini ? '' : 'Published' }</Tag> :
        <Tag { ...props } color='volcano' icon={ <EyeInvisibleOutlined /> }>{ mini ? '' : 'Unpublished' }</Tag> ;
};

const StickyTag = ( { isSticky, ...props } ) => {

    return isSticky ? 
        <ColoredTag { ...props } icon={ <VerticalAlignTopOutlined /> } value={ { isSticky } }>Sticky</ColoredTag> : <></> ;
};

const EnabledTag = ( { isEnabled, ...props } ) => {

    return isEnabled ?
        <Tag { ...props } color='green' icon={ <CheckCircleOutlined /> }>Enabled</Tag> :
        <Tag { ...props } color='default' icon={ <StopOutlined /> }>Disabled</Tag> ;
};

const FeaturedTag = ( { isFeatured, ...props } ) => {
    return isFeatured ? <Tag { ...props } icon={ <StarOutlined /> } color='gold' >Featured</Tag> : <></>;
};

const RepeatableTag = ( { isRepeatable, ...props } ) => {
    return isRepeatable ?
        <ColoredTag { ...props } icon={ <ReloadOutlined /> } value={ { isRepeatable: true } }>Repeatable</ColoredTag> :
        <ColoredTag { ...props } value={ { isRepeatable: false } }>Not Repeatable</ColoredTag>;
};

const SequentialTag = ( { isSequential, ...props } ) => {
    return isSequential ?
        <ColoredTag { ...props } value={ { isSequential: true } }>Sequential</ColoredTag> :
        <ColoredTag { ...props } value={ { isSequential: false } }>Non-Sequential</ColoredTag>;
};

const BrandTag = ( { brand, ...props } ) => {
    return brand ? <ColoredTag { ...props } value={ brand.id }>{ brand.name }</ColoredTag> : null;
};

// where generic uncomplicated icon tags go
const Icon = ( { value: _value, children, ...props } ) => {
    const value = _value.toLowerCase();

    const [ icon, name ] = 
        value === 'file' ? [ <FileOutlined />, 'File' ] :
        value === 'url' ? [ <LinkOutlined />, 'URL' ]  :
        [ undefined, undefined ];

    return <ColoredTag icon={ icon } value={ value } { ...props } >{ children ?? name }</ColoredTag>;
};

ColoredTag.Publish = PublishTag;
ColoredTag.Enabled = EnabledTag;
ColoredTag.Featured = FeaturedTag;
ColoredTag.Repeatable = RepeatableTag;
ColoredTag.Sequential = SequentialTag;
ColoredTag.Icon = Icon;
ColoredTag.Sticky = StickyTag;
ColoredTag.Brand = BrandTag;

export const ViewPageActions = ( {
    item,
    editDisabled = false, handleEditCb, 
    deleteDisabled = false, handleDeleteCb 
} ) => {

    async function handleEditClick( item ) {
        if ( !editDisabled ) {
            await handleEditCb?.( item );
        }
    }

    async function handleDeleteClick( item ) {
        if ( !deleteDisabled ) {
            await handleDeleteCb?.( item );
        }
    }

    return <Dropdown
        trigger={ [ 'click' ] }
        menu={ {
            className: 'action-dropdown',
            items: [
                {
                    key: 'edit',
                    label: 'Edit',
                    icon: <EditOutlined />,
                    disabled: editDisabled,
                    onClick: () => handleEditClick( item ),
                }, 
                {
                    key: 'delete',
                    danger: true,
                    className: 'pop-confirm-action',
                    disabled: deleteDisabled,
                    label: <Popconfirm
                        disabled={ deleteDisabled }
                        title="Are you sure you want to delete this?"
                        onConfirm={ () => handleDeleteClick( item ) }
                        okText="Yes"
                        cancelText="No"
                    >
                        <DeleteOutlined /><span>Delete</span>
                    </Popconfirm>,
                },
            ]
        } }
    >
        <MoreOutlined className='clickable' />
    </Dropdown>;
};